import {
  AUTH_CHANGE_PLAN,
  AUTH_CHANGE_PROFILE,
  AUTH_CHANGE_LOADING,
  AUTH_FAILED,
  AUTH_LOGIN,
  AUTH_USER,
} from '../actions/_actionTypes'

import { PATIENT, PROFESSIONAL, ADMINISTRATOR, SECRETARY, TVTVADMN, TVTVPACI } from '../../settings/_profileSettings'
import { asyncError, asyncRequest, setGenericError } from './'
import axios from '../../settings/axios'
import { history } from '../../routers/AppRouter'
import * as routes from '../../settings/_routesSettings'
import { AWAITING_PIN, AWAITING_MEDICAL_REQUEST, INACTIVE } from '../../settings/_personSettings'
import { apiEndpoints } from '../../settings/_apiSettings'
import { setAuthToken, deleteAuthToken, getAuthToken, deleteFlagFromSessionStorage } from 'utils/authHelper'
import * as Sentry from '@sentry/browser'
import { DISMISSED_PATIENT_REQUIRE_INFO_MODAL, PATIENT_SCHEDULE_STATUS, PATIENT_SAVED_PLAN } from 'settings/_storageFlagsSettings'
import { MIXPANEL_EXP_CONFIG_KEY, mixpanelInstance } from 'settings/mixpanel'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'

const {
  VITTA
} = PlanGroupCategoryEnum

/* User login action */
export const authLogin = (loginData, redirectTo, signupSuccess) => async dispatch => {
  const successRedirect = signupSuccess ? routes.REGISTER_SUCCESS_ATTACH_MEDICAL_REQUEST : routes.REGISTER_ATTACH_MEDICAL_REQUEST
  const fetchUrl = apiEndpoints.AUTH
  const fetchData = { ...loginData }
  dispatch(asyncRequest())

  try {
    const response = await axios.post(fetchUrl, fetchData)

    if (response.status === 'OK') {
      const userToken = response.data.jwt
      const requireFaceId = response.data.require_faceid
      const payload = { userToken }


      setAuthToken(userToken)

      await dispatch({
        type: AUTH_LOGIN,
        payload,
      })

      const meData = await dispatch(authUser(userToken))

      if (!meData.profileSelected) {
        Sentry.captureMessage('Erro no /me', { extra: { ...meData } })
      }

      mixpanelInstance.identify(meData?.userData?.session.sub)
      mixpanelInstance.people.set({
        $email: meData?.userData?.person_general.email,
        $name: meData?.userData?.person_general?.full_name
      })

      const planGroupCode = meData.planSelected?.plan_group?.code

      const usesNewMedicalRequestScreen = meData.planSelected?.uses_new_medical_request_screen

      if (redirectTo)
        history.push(redirectTo)
      else if (meData.profileSelected.status_code === AWAITING_PIN)
        history.push(routes.RESEND_PIN)
      else if (meData.profileSelected.status_code === AWAITING_MEDICAL_REQUEST)
        if (usesNewMedicalRequestScreen) {
          history.push(routes.REGISTER_MEDICAL_REQUEST)
        } else {
          history.push(successRedirect)
        }
      else if (requireFaceId)
        history.push(routes.FACE_ID)
      else if (meData.userData.session.profile_role_group_active === PATIENT)
        history.push(routes.DASHBOARD_HOME_PATIENT)
      else if (meData.userData.session.profile_role_group_active === PROFESSIONAL)
        history.push(routes.DASHBOARD_HOME)
      else if (meData.userData.session.profile_role_active === SECRETARY)
        history.push(routes.DASHBOARD_GENERAL_VIEW)
      else if (meData.userData.session.profile_role_active === ADMINISTRATOR) {
        window.open(process.env.REACT_APP_BACKOFFICE_URL, '_blank')
        dispatch(authLogout())
      } else {
        history.push(routes.APP)
      }
    } else {
      let authFailedPayload = response.messages[0].message
      dispatch(authFailed(authFailedPayload))
      dispatch({ type: AUTH_CHANGE_LOADING, payload: { isLoading: false } })
    }
  } catch (err) {
    Sentry.captureException(err)
    dispatch(setGenericError('Não foi possível se autenticar. Entre em contato através do nosso chat para completar seu cadastro.'))
  }
}

/* User logout action */
export const authLogout = () => async (dispatch) => {
  const fetchUrl = apiEndpoints.LOGOUT
  const currentToken = getAuthToken()

  Sentry.configureScope(scope => scope.setUser(null))
  await axios.post(fetchUrl, { jwt: currentToken })

  mixpanelInstance.reset()

  deleteAuthToken()
  deleteFlagFromSessionStorage(DISMISSED_PATIENT_REQUIRE_INFO_MODAL)
  deleteFlagFromSessionStorage(PATIENT_SCHEDULE_STATUS)
  window.localStorage.removeItem(PATIENT_SAVED_PLAN)
  window.localStorage.removeItem(MIXPANEL_EXP_CONFIG_KEY)
  window.location.href = routes.HOME_INSTITUCIONAL
}

/** User authentication action */
export const authUser = (userToken) => async dispatch => {
  let fetchUrl = apiEndpoints.ME
  dispatch(asyncRequest())

  try {
    const response = await axios.get(fetchUrl)

    if (response.status === 'OK') {
      const userData = response.data
      const activeProfile = userData.session.profile_active
      const profileSelected = userData.person_profile.find(profile => profile.profile_code === activeProfile)

      const isPaci = activeProfile === TVTVPACI

      const patientSavedPlan = isPaci ? localStorage.getItem(PATIENT_SAVED_PLAN) : null

      const patientDefaultPlan = profileSelected?.plans?.find(plan => plan.default === true)

      const isDefaultPlanParticularOrVitta = (patientDefaultPlan?.partner_plan_is_particular || patientDefaultPlan?.plan_group?.category === VITTA)

      const nextActivePlan = profileSelected?.plans?.find(plan => !plan.partner_plan_is_particular && plan.status_code !== INACTIVE && plan.plan_group.category !== VITTA)

      let currentPlan = patientDefaultPlan

      const currentSavedPlan = profileSelected?.plans?.find(plan => plan.partner_plan_code === patientSavedPlan)
      const isSavedPlanActive = currentSavedPlan ? currentSavedPlan?.status_code !== INACTIVE : false

      if (isPaci) {
        if (isSavedPlanActive) {
          currentPlan = currentSavedPlan
        } else {
          if (nextActivePlan) {
            currentPlan = nextActivePlan
            localStorage.setItem(PATIENT_SAVED_PLAN, nextActivePlan.partner_plan_code)
          } else {
            currentPlan = patientDefaultPlan
          }
        }
      }

      const profilePermissions = profileSelected.permissions

      let profileSelectedPlans = [...profileSelected.plans]

      const hasParticularPlan = profileSelected.plans.some(plan => plan.partner_plan_is_particular || plan.plan_group.category === VITTA)

      if (!patientSavedPlan && isPaci && !isDefaultPlanParticularOrVitta) {
        localStorage.setItem(PATIENT_SAVED_PLAN, currentPlan.partner_plan_code)
      }

      if (!hasParticularPlan && isPaci) {
        const response = await axios.post(apiEndpoints.LINK_PRIVATE_PLAN(userData.username))
        profileSelectedPlans = [...profileSelectedPlans, response.data]
      }

      const payload = {
        planSelected: patientDefaultPlan,
        profilePermissions,
        profileSelected: {
          ...profileSelected,
          plans: profileSelectedPlans
        },
        userData,
        userToken,
      }

      dispatch({ type: AUTH_USER, payload })

      return payload
    } else {
      dispatch(asyncError(response.messages))
    }
  } catch (err) {
    dispatch(asyncError(err))
  }
}

/* User authentication action */
export const authSwap = (profile) => async dispatch => {
  if (profile === TVTVADMN) {
    return window.open(process.env.REACT_APP_BACKOFFICE_URL, '_blank')
  }

  let fetchUrl = apiEndpoints.ME
  let postData = { 'profile_code': profile }
  dispatch(asyncRequest())

  try {
    const response = await axios.post(fetchUrl, postData)

    if (response.status === 'OK') {
      setAuthToken(response.data.jwt)
      dispatch({ type: AUTH_CHANGE_PROFILE })
      window.location.href = routes.DASHBOARD
    } else {
      dispatch(asyncError(response.messages))
    }
  } catch (err) {
    dispatch(asyncError(err))
  }
}

/* User authentication action */
export const authChangePlan = (plan) => dispatch => {
  const payload = { planSelected: plan }

  dispatch({
    type: AUTH_CHANGE_PLAN,
    payload,
  })

}

/* Authentication failed */
export const authFailed = (payload) => {
  if (!payload) payload = ''

  return {
    type: AUTH_FAILED,
    payload,
  }
}
